//React
import React, { useMemo } from 'react';

//Mantine
import {
  AppShell,
  useComputedColorScheme,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';

//Refine
import type { RefineThemedLayoutV2Props } from '@refinedev/mantine';
import { ThemedLayoutContextProvider } from '@refinedev/mantine';
//components
import { useSiderStore } from 'store/SiderStore';
import { useIsMobile } from 'hooks';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'components/common/ErrorFallback';
import { BANNER_HEIGHT, isLocalProd } from 'components/common/LocalProdBanner';
import { UpdateBanner } from 'components/common/UpdateBanner';

export const Layout: React.FC<RefineThemedLayoutV2Props> = ({
  Sider,
  Header,
  Title,
  Footer,
  OffLayoutArea,
  initialSiderCollapsed = false,
  children,
}) => {
  const { toggleColorScheme } = useMantineColorScheme();
  useHotkeys([['mod+j', () => toggleColorScheme()]]);

  const theme = useMantineTheme();
  const isMobile = useIsMobile(700);
  const { isCollapsed } = useSiderStore();

  const [mobileOpened] = useDisclosure(false);
  const [desktopOpened] = useDisclosure(true);

  const drawerWidth = useMemo(() => {
    if (isMobile) return '0rem';
    return isCollapsed ? '3.125rem' : '12.5rem';
  }, [isMobile, isCollapsed]);

  const computedColorScheme = useComputedColorScheme('dark');
  const borderColor = useMemo(
    () =>
      computedColorScheme === 'dark'
        ? theme.colors.dark[5]
        : theme.colors.gray[3],
    [computedColorScheme, theme.colors.dark, theme.colors.gray]
  );

  return (
    <ThemedLayoutContextProvider initialSiderCollapsed={initialSiderCollapsed}>
      <AppShell
        navbar={{
          width: '18.75rem',
          breakpoint: '43.75rem',
          collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
        }}
        header={{ height: '3.75rem' }}
        styles={{
          main: {
            marginLeft: drawerWidth,
            transition: 'margin-left 0.3s',
            paddingInline: 0,
            width: 'auto',
          },
          navbar: {
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            paddingLeft: !isCollapsed ? '0.25rem' : 0,
          },
        }}
        style={{
          backgroundImage: `url(/background-${computedColorScheme}.svg)`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
        withBorder
        w={'100%'}
      >
        <AppShell.Header
          zIndex={200}
          py={6}
          px="sm"
          mb="xl"
          bg={'transparent'}
          style={{
            backdropFilter: 'blur(20px)',
            justifyContent: 'space-between',
            top: isLocalProd ? `${BANNER_HEIGHT}px` : 0,
          }}
          display={'flex'}
          w={'100%'}
        >
          <Header />
        </AppShell.Header>
        <AppShell.Navbar
          style={{
            width: drawerWidth,
            transition: 'width 200ms ease, minWidth 200ms ease',
            position: 'fixed',
            top: isLocalProd ? `${BANNER_HEIGHT}px` : 0,
            height: isLocalProd ? `calc(100vh - ${BANNER_HEIGHT}px)` : '100vh',
            borderRight: 0,
            zIndex: 199,
            paddingTop: '3rem',
            border: `1px solid ${borderColor}`,
            backdropFilter: 'blur(20px)',
          }}
          bg={'transparent'}
        >
          <Sider Title={Title} />
        </AppShell.Navbar>
        <AppShell.Main className="root">
          <UpdateBanner />
          <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onReset={() => (window.location.href = '/')}
          >
            {children}
          </ErrorBoundary>
        </AppShell.Main>
        {Footer && <Footer />}
        {OffLayoutArea && <OffLayoutArea />}
      </AppShell>
    </ThemedLayoutContextProvider>
  );
};
